import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useAuth } from '@/features/auth';
import { supabase } from '@/lib/supabase';

const DEFAULT_COLORS = {
  orange: '245 158 17',
  teal: '11 148 136',
  dark: '31 41 55',
};

type CustomColors = { orange?: string; teal?: string; dark?: string };

interface ThemeContextType {
  customColors: CustomColors | null;
  setCustomColors: (colors: CustomColors) => Promise<void>;
  resetColors: () => Promise<void>;
  applyColors: (colors: CustomColors | null) => void;
}

const ThemeContext = createContext<ThemeContextType | null>(null);

function applyColorsToDOM(colors: CustomColors | null) {
  const root = document.documentElement;
  const resolved = {
    orange: colors?.orange || DEFAULT_COLORS.orange,
    teal: colors?.teal || DEFAULT_COLORS.teal,
    dark: colors?.dark || DEFAULT_COLORS.dark,
  };
  root.style.setProperty('--pp-orange', resolved.orange);
  root.style.setProperty('--pp-teal', resolved.teal);
  root.style.setProperty('--pp-dark', resolved.dark);
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [customColors, setCustomColorsState] = useState<CustomColors | null>(null);

  // Load custom_colors from profile on mount / user change
  useEffect(() => {
    if (!user?.id) {
      applyColorsToDOM(null);
      setCustomColorsState(null);
      return;
    }

    let cancelled = false;
    supabase
      .from('profiles')
      .select('custom_colors')
      .eq('id', user.id)
      .single()
      .then(({ data }) => {
        if (cancelled) return;
        const colors = data?.custom_colors as CustomColors | null;
        setCustomColorsState(colors);
        applyColorsToDOM(colors);
      });

    return () => { cancelled = true; };
  }, [user?.id]);

  const setCustomColors = async (colors: CustomColors) => {
    if (!user?.id) return;
    setCustomColorsState(colors);
    applyColorsToDOM(colors);
    await supabase.from('profiles').update({ custom_colors: colors as Record<string, string> }).eq('id', user.id);
  };

  const resetColors = async () => {
    if (!user?.id) return;
    setCustomColorsState(null);
    applyColorsToDOM(null);
    await supabase.from('profiles').update({ custom_colors: null }).eq('id', user.id);
  };

  const applyColors = (colors: CustomColors | null) => {
    applyColorsToDOM(colors);
  };

  return (
    <ThemeContext.Provider value={{ customColors, setCustomColors, resetColors, applyColors }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}

export { DEFAULT_COLORS };
